import CaretDownOutlined from '@ant-design/icons/CaretDownOutlined';
import { useGetDevices } from '@vindr/services/actions/devices.action';
import { ResponseFetchStudyList } from '@vindr/services/apis/study.api';
import { FILM_TYPE } from '@vindr/services/constants/print.constant';
import { REPORT_STATUS } from '@vindr/services/constants/report.constant';
import { formatDateColumn } from '@vindr/utils/dateTime';
import { Collapse, Form, FormInstance, InputNumber, Select, Typography } from 'antd';
import { isEqual } from 'lodash';
import { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FormItem from '../FormItem/FormItem';
import { ReportFormStates } from './ReportForm';
const { Title } = Typography;

interface PanelInfoProps {
  report: ReportFormStates['selectedReport'];
  form: FormInstance<any>;
  order: ReportFormStates['SelectedOrderType'];
  study: ResponseFetchStudyList['data'][number];
}

const PanelInfo = ({ report, form, order, study }: PanelInfoProps) => {
  const { t } = useTranslation('Vindoc');
  const [expand, setExpand] = useState(true);
  const { data: deviceResponse } = useGetDevices(
    { limit: 100, offset: 0 },
    { enabled: !order?.device?.name }
  );

  const filmQuantity = Form.useWatch('film_quantity', form);
  const filmType = Form.useWatch('film_type', form);

  const info = [
    study
      ? { label: t('Study Date'), value: formatDateColumn(study?.study_date) }
      : { label: t('Scheduled Date'), value: formatDateColumn(order?.scheduled_date) },
    { label: t('Procedure Name'), value: report?.procedure?.name || order?.procedure?.name || '' },
    { label: t('Diagnostic'), value: report?.order?.diagnostic || order?.diagnostic || '' },
    {
      label: t('Device'),
      value:
        order?.device?.name ||
        deviceResponse?.data.find((it) => it.id === study?.device_id)?.name ||
        '',
    },
    // { label: t('Status'), value: REPORT_STATUS[report?.status] },
    {
      label: t('Film Type'),
      value: (
        <div className="grid flex-1 grid-cols-1 sm:grid-cols-2" style={{ gridGap: 8 }}>
          <Form.Item name="film_type" className="flex-1" noStyle>
            <Select
              size="small"
              className="flex-1"
              placeholder={t('Select')}
              suffixIcon={<CaretDownOutlined />}
              disabled={report?.status !== REPORT_STATUS.READING}
              allowClear
              // defaultValue={report?.film_type}
              onChange={(value) => {
                if (typeof value !== 'number') return form.setFieldValue('film_quantity', 0);
                if (!filmQuantity || filmQuantity < 1)
                  return form.setFieldValue('film_quantity', 1);
              }}
            >
              {FILM_TYPE.map((it, index) => (
                <Select.Option value={index} key={index}>
                  {it?.title}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <div className="flex flex-1 space-x-2">
            <div>{t('Quantity')}: </div>
            <Form.Item name="film_quantity" noStyle>
              <InputNumber
                className="flex-1"
                disabled={report?.status !== REPORT_STATUS.READING}
                size="small"
                min={typeof filmType === 'number' ? 1 : 0}
              />
            </Form.Item>
          </div>
        </div>
      ),
    },
    {
      label: t('Technician'),
      value: (
        <FormItem.Technician
          disabled={report?.status !== REPORT_STATUS.READING}
          {...(report?.technician?.id && {
            selected: {
              value: report?.technician?.id,
              label: report?.technician?.fullname || report?.technician?.username,
            },
          })}
          noStyle
          required={false}
          label={null}
        />
      ),
    },
    {
      label: t('Submitted By'),
      /**
       * ? When have Submitter name, show Submitted by
       * ? When report status [reading]: Not info submitter -> show empty
       * ? When report status [submitted]: Not info submitter -> show Unknown name, submitted_at
       */
      value: report?.submitter?.fullname
        ? formatByTitle(report, 'submitter')
        : report && report?.status !== REPORT_STATUS.READING
        ? `Unknown, ${formatDateColumn(report?.submitted_at)}`
        : '',
    },
    {
      label: t('Approved By'),
      /**
       * ? When have Approver name, show Approved by
       * ? When report status [reading, submitted]: Not info approver -> show empty
       * ? When report status [approved], but not have Approver name -> show Unknown name, approved_at
       */
      value: report?.approver?.fullname
        ? formatByTitle(report, 'approver')
        : report && report?.status === REPORT_STATUS.APPROVED
        ? `Unknown, ${formatDateColumn(report?.approved_at)}`
        : '',
    },
  ];

  return (
    <Collapse
      activeKey={expand ? ['1'] : []}
      expandIconPosition="end"
      className="report-result p-0"
      onChange={(key) => setExpand(key && key.length > 0)}
    >
      <Collapse.Panel
        header={
          <Title level={4} className="mb-0">
            {t('Information')}
          </Title>
        }
        className="list-thumbnails"
        key={1}
      >
        <Form
          className="grid grid-cols-1"
          style={{ gridGap: 8 }}
          form={form}
          initialValues={{
            film_quantity: 0,
          }}
        >
          {info.map((it, index) => (
            <div className="flex flex-wrap" key={index}>
              <div className="min-w-20 basis-[108px]">{it.label}:</div>
              <div className="flex min-w-20 max-w-full flex-1 shrink-0 flex-wrap">
                {it.value || ''}
              </div>
            </div>
          ))}
        </Form>
      </Collapse.Panel>
    </Collapse>
  );
};

function formatByTitle(report, type: 'approver' | 'submitter') {
  const dateType = {
    approver: 'approved_at',
    submitter: 'submitted_at',
  };

  return `${report?.[type]?.title || ''} ${report?.[type]?.fullname || ''}${
    report?.[type]?.fullname ? `, ${formatDateColumn(report?.[dateType?.[type]])}` : ''
  }`;
}

export default memo(
  PanelInfo,
  (prev, next) =>
    isEqual(prev.order, next.order) &&
    isEqual(prev.report, next.report) &&
    isEqual(prev.study, next.study)
);
